// hooks/useTasks.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Task } from "../../types/tasks";
import { createTask, deleteTask, getTasks, updateTask } from "../api/tasks";

interface CreateTaskPayload {
  taskData: Partial<Task>;
}

interface UpdateTaskPayload {
  taskId: number;
  taskData: Partial<Task>;
}

interface DeleteTaskPayload {
  taskId: number;
}

export const useTasks = () => {
  const queryClient = useQueryClient();

  /**
   * Fetch all tasks with optional filters.
   */
  const {
    data: tasks,
    isLoading: isLoadingTasks,
    isError: isErrorTasks,
    error: tasksError,
  } = useQuery<Task[], Error>({
    queryKey: ["tasks"],
    queryFn: () => getTasks(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  /**
   * Mutation to create a new task.
   */
  const createTaskMutation = useMutation<Task, Error, CreateTaskPayload>({
    mutationFn: async ({ taskData }) => {
      const newTask = await createTask(taskData);
      return newTask;
    },
    onSuccess: () => {
      // Invalidate and refetch tasks
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (error) => {
      console.error("Error creating task:", error);
    },
  });

  /**
   * Mutation to update an existing task.
   */
  const updateTaskMutation = useMutation<Task, Error, UpdateTaskPayload>({
    mutationFn: async ({ taskData }) => {
      const updatedTask = await updateTask(taskData);
      return updatedTask;
    },
    onSuccess: (updatedTask) => {
      // Update the specific task in the cache
      queryClient.setQueryData<Task[] | undefined>(["tasks"], (oldTasks) =>
        oldTasks
          ? oldTasks.map((task) =>
              task.id === updatedTask.id ? updatedTask : task
            )
          : []
      );
    },
    onError: (error) => {
      console.error("Error updating task:", error);
    },
  });

  /**
   * Mutation to delete a task.
   */
  const deleteTaskMutation = useMutation<
    { id: number } | null,
    Error,
    DeleteTaskPayload
  >({
    mutationFn: async ({ taskId }) => {
      const deletedTask = await deleteTask(taskId);
      return deletedTask;
    },
    onSuccess: (deletedTask) => {
      if (deletedTask) {
        // Remove the deleted task from the cache
        queryClient.setQueryData<Task[] | undefined>(["tasks"], (oldTasks) =>
          oldTasks ? oldTasks.filter((task) => task.id !== deletedTask.id) : []
        );
      }
    },
    onError: (error) => {
      console.error("Error deleting task:", error);
    },
  });

  /**
   * Function to create a new task.
   *
   * @param taskData - The data for the new task.
   */
  const addTask = (taskData: Partial<Task>) => {
    createTaskMutation.mutate({ taskData });
  };

  /**
   * Function to update an existing task.
   *
   * @param taskId - The ID of the task to update.
   * @param taskData - The updated data for the task.
   */
  const modifyTask = (taskId: number, taskData: Partial<Task>) => {
    updateTaskMutation.mutate({ taskId, taskData });
  };

  /**
   * Function to delete a task.
   *
   * @param taskId - The ID of the task to delete.
   */
  const removeTask = (taskId: number) => {
    deleteTaskMutation.mutate({ taskId });
  };

  return {
    tasks,
    isLoadingTasks,
    isErrorTasks,
    tasksError,
    addTask,
    isCreating: createTaskMutation.isPending,
    isCreateError: createTaskMutation.isError,
    createError: createTaskMutation.error,
    modifyTask,
    isUpdating: updateTaskMutation.isPending,
    isUpdateError: updateTaskMutation.isError,
    updateError: updateTaskMutation.error,
    removeTask,
    isDeleting: deleteTaskMutation.isPending,
    isDeleteError: deleteTaskMutation.isError,
    deleteError: deleteTaskMutation.error,
  };
};
